/**
 * Language Selector Component
 */
import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { languages, getStoredLanguage, setStoredLanguage } from '../utils/translations';

export const LanguageSelector = ({ onChange, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');

  useEffect(() => {
    const stored = getStoredLanguage();
    setCurrentLang(stored);
  }, []);

  const handleSelect = (code) => {
    setCurrentLang(code);
    setStoredLanguage(code);
    setIsOpen(false);
    if (onChange) onChange(code);
  };

  const current = languages.find(l => l.code === currentLang) || languages[0];

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm transition-all border border-gray-200"
          data-testid="language-selector"
        >
          <span className="text-base">{current.flag}</span>
          <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 min-w-[150px]">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                    currentLang === lang.code ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-all"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span>{current.flag}</span>
        <span>{current.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-50 py-2 min-w-[180px]">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-secondary flex items-center gap-3 ${
                  currentLang === lang.code ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
